import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnInit,
    signal,
    WritableSignal,
} from '@angular/core';
import { TelXLButtonComponent } from '@dxp/elements';
import {
    IEmailMessageHistory,
    IHistoricActivity,
    IMessageHistory,
} from '@dxp/core/models';
import { formatDate, isToday } from 'date-fns';
import { finalize, Observable, take, tap } from 'rxjs';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { SETTINGS_STORE, USER_STORE } from '@dxp/core/stores';
import { ActivityHistoryService } from '../../services/activity-history.service';
import { TelXLChatHistoryComponent } from './chat-history/chat-history.component';
import { TelXLEmailHistoryComponent } from './email-history/email-history.component';

@Component({
    selector: 'telxl-shared-activity-history-conversation',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TelXLButtonComponent,
        TelXLChatHistoryComponent,
        TelXLEmailHistoryComponent,
        NgbTooltip,
    ],
    templateUrl: './activity-history-conversation.component.html',
    styleUrl: './activity-history-conversation.component.scss',
})
export class TelXLActivityHistoryConversationComponent implements OnInit {
    readonly #activityHistoryService = inject(ActivityHistoryService);
    readonly #userStore = inject(USER_STORE);
    readonly #settingsStore = inject(SETTINGS_STORE);

    activity = input.required<IHistoricActivity>();

    activityMessages$?: Observable<IMessageHistory[] | IEmailMessageHistory[]>;
    activityMessages: WritableSignal<
        IMessageHistory[] | IEmailMessageHistory[]
    > = signal<IMessageHistory[] | IEmailMessageHistory[]>([]);

    readonly displayNote = signal<boolean>(false);
    readonly summary = signal<string | null>(null);
    readonly loading = signal(false);
    readonly summarising = signal<boolean>(false);

    readonly hasAvailableNote = computed(() => !!this.activity().notes);

    readonly dateOfChat = computed<string>(() => {
        if (this.activityMessages().length > 0) {
            const firstMessage = this.activityMessages()[0];
            const firstMessageDate = new Date(firstMessage.creationDate);

            return isToday(firstMessageDate)
                ? 'Today'
                : formatDate(firstMessageDate, 'do MMMM yyyy');
        } else {
            return '';
        }
    });

    readonly timeOfChat = computed<string>(() => {
        if (this.activityMessages().length > 0) {
            const firstMessage = this.activityMessages()[0];
            const firstMessageDate = new Date(firstMessage.creationDate);

            return formatDate(firstMessageDate, 'h:mmbbb');
        } else {
            return '';
        }
    });

    readonly isChatHistory = computed<boolean>(() =>
        this.activityMessages().length > 0
            ? 'id' in this.activityMessages()[0]
            : false,
    );

    readonly chatActivityMessages = computed<IMessageHistory[]>(
        () => this.activityMessages() as IMessageHistory[],
    );

    readonly emailActivityMessages = computed<IEmailMessageHistory[]>(
        () => this.activityMessages() as IEmailMessageHistory[],
    );

    readonly isAiSummaryEnabled = computed<boolean>(
        () =>
            this.#settingsStore.isAiSummaryEnabled() &&
            this.#userStore.allowAgentsToSummariseInHistory() &&
            !this.activity().notesFromAISummary,
    );

    readonly isAiSummaryEnabledToolTip = computed<string | null>(() => {
        if (this.activity().notesFromAISummary)
            return 'Summary already generated. See notes';

        if (!this.#settingsStore.isAiSummaryEnabled())
            return 'License upgrade required';

        if (!this.#userStore.allowAgentsToSummariseInHistory())
            return 'This feature is disabled for agents';

        return null;
    });

    ngOnInit() {
        const id = this.activity().id;

        this.activityMessages$ =
            this.activity().channelType === 'email'
                ? this.#activityHistoryService.fetchConversationEmailHistory(id)
                : this.#activityHistoryService.fetchConversationChatHistory(id);

        this.activityMessages$
            .pipe(tap(messages => this.activityMessages.set(messages)))
            .subscribe();
    }

    download() {
        if (this.activity().channelType === 'email') {
            this.activityMessages$?.pipe(take(1)).subscribe(emailMessages => {
                this.#activityHistoryService.downloadEmailTranscript(
                    emailMessages as IEmailMessageHistory[],
                );
            });
        } else {
            this.activityMessages$?.pipe(take(1)).subscribe(chatMessages => {
                this.#activityHistoryService.downloadChatTranscript(
                    this.activity().id,
                    chatMessages as IMessageHistory[],
                );
            });
        }
    }

    toggleSummaryTooltip(tt: NgbTooltip) {
        if (tt.isOpen()) {
            tt.close();
            return;
        }

        this.loading.set(true);
        this.summary.set(null);
        tt.open();

        const id = this.activity().id;
        const media =
            this.activity().channelType === 'email' ? 'email' : 'chat';

        this.#activityHistoryService
            .fetchConversationSummary(id, media)
            .pipe(
                take(1),
                finalize(() => this.loading.set(false)),
            )
            .subscribe({
                next: ({ summary }) => {
                    this.summary.set(summary ?? 'No summary available');
                },
                error: () => {
                    this.summary.set('No summary available');
                },
            });
    }

    toggleNoteDisplay(): void {
        const currentNoteState = !this.displayNote();
        this.displayNote.set(currentNoteState);
    }
}
